import { useCallback, useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useParams, useNavigate } from "react-router-dom";
import { Formik } from "formik";
import axios from "axios";
import ReactDOMServer from "react-dom/server";
import {
  AppCard,
  AppDatePicker,
  AppDeleteModal,
  AppMultiSelect,
  AppNoInfo,
  AppNotificationAlert,
  AppRemixIcon,
  AppSingleSelect,
  AppTextArea,
} from "@/layout/AppComponents";
import styled from "styled-components";
import { axiosPath, path } from "../../../../routing/helpers/router";
import Translator from "../../../../layout/components/helpers/Translator";
import BreadcrumbHandler from "../../../../layout/components/breadcrumb/BreadcrumbHandler";
import * as currentPageAction from "../../../../store/actions/currentPageAction";
import ModuleComsaEnvironmentalSideBarReports from "../ModuleComsaEnvironmentalSideBarReports";
import ModuleComsaEnvironmentalRailwayConstruction from "../../../../layout/screenSpecifics/Modules/ComsaEnvironmental/ModuleComsaEnvironmentalRailwayConstruction";
import AppSubmitButton from "../../../../layout/components/form/AppSubmitButton";
import ModuleComsaEnvironmentalCivilConstruction from "../../../../layout/screenSpecifics/Modules/ComsaEnvironmental/ModuleComsaEnvironmentalCivilConstruction";
import ModuleComsaEnvironmentalUrbanization from "../../../../layout/screenSpecifics/Modules/ComsaEnvironmental/ModuleComsaEnvironmentalUrbanization";
import ModuleComsaEnvironmentalBuilding from "../../../../layout/screenSpecifics/Modules/ComsaEnvironmental/ModuleComsaEnvironmentalBuilding";
import ModuleComsaEnvironmentalMaritime from "../../../../layout/screenSpecifics/Modules/ComsaEnvironmental/ModuleComsaEnvironmentalMaritime";
import errorHandler from "@/layout/components/error/ErrorHandler";
import useIsMounted from "../../../../layout/components/hooks/useIsMounted";
import AppLink from "../../../../routing/helpers/AppLink";
import _ from "lodash";

export default function ModuleComsaEnvironmentalConstructionDataInputScreen() {
  const [deleteSectionPassword] = useState("mjKr3@i4");
  const { moduleBridgeHex, subModuleBridgeHex, plantHex } = useParams();
  const dispatch = useDispatch();
  const isMounted = useIsMounted();
  const navigate = useNavigate();
  const moduleData = useSelector((state) => state.currentPage?.moduleData);
  const locale = useSelector((state) => state.global.urlData.lang);
  const global = useSelector((state) => state.global);
  const [initialValues, setInitialValues] = useState();
  const [showModalDeleteSectionModal, setShowModalDeleteSectionModal] =
    useState();
  const [noYesOptions] = useState([
    {
      label: ReactDOMServer.renderToString(
        <Translator file="module-comsaEnvironmental" name="no" />
      ),
      value: 0,
    },
    {
      label: ReactDOMServer.renderToString(
        <Translator file="module-comsaEnvironmental" name="yes" />
      ),
      value: 1,
    },
  ]);
  const [controlInfluenceOptions] = useState([
    {
      label: ReactDOMServer.renderToString(
        <Translator file="module-comsaEnvironmental" name="control" />
      ),
      value: 0,
    },
    {
      label: ReactDOMServer.renderToString(
        <Translator file="module-comsaEnvironmental" name="influence" />
      ),
      value: 1,
    },
  ]);
  const [constructionScopeOptions] = useState([
    {
      label: ReactDOMServer.renderToString(
        <Translator file="module-comsaEnvironmental" name="urban" />
      ),
      value: 0,
    },
    {
      label: ReactDOMServer.renderToString(
        <Translator file="module-comsaEnvironmental" name="rural" />
      ),
      value: 1,
    },
    {
      label: ReactDOMServer.renderToString(
        <Translator file="module-comsaEnvironmental" name="special" />
      ),
      value: 2,
    },
  ]);
  const [tipologyOptions] = useState([
    {
      label: ReactDOMServer.renderToString(
        <Translator file="module-comsaEnvironmental" name="railway" />
      ),
      value: 0,
    },
    {
      label: ReactDOMServer.renderToString(
        <Translator file="module-comsaEnvironmental" name="civil" />
      ),
      value: 1,
    },
    {
      label: ReactDOMServer.renderToString(
        <Translator file="module-comsaEnvironmental" name="urbanization" />
      ),
      value: 2,
    },
    {
      label: ReactDOMServer.renderToString(
        <Translator file="module-comsaEnvironmental" name="building" />
      ),
      value: 3,
    },
    {
      label: ReactDOMServer.renderToString(
        <Translator file="module-comsaEnvironmental" name="maritime" />
      ),
      value: 4,
    },
  ]);

  const [affectedServicesOptions] = useState([
    {
      label: ReactDOMServer.renderToString(
        <Translator file="module-comsaEnvironmental" name="gas" />
      ),
      value: 0,
    },
    {
      label: ReactDOMServer.renderToString(
        <Translator file="module-comsaEnvironmental" name="water" />
      ),
      value: 1,
    },
    {
      label: ReactDOMServer.renderToString(
        <Translator file="module-comsaEnvironmental" name="electricity" />
      ),
      value: 2,
    },
    {
      label: ReactDOMServer.renderToString(
        <Translator file="module-comsaEnvironmental" name="telephone" />
      ),
      value: 3,
    },
    {
      label: ReactDOMServer.renderToString(
        <Translator file="module-comsaEnvironmental" name="optical.fibre" />
      ),
      value: 4,
    },
    {
      label: ReactDOMServer.renderToString(
        <Translator file="module-comsaEnvironmental" name="oil.pipeline" />
      ),
      value: 5,
    },
    {
      label: ReactDOMServer.renderToString(
        <Translator file="module-comsaEnvironmental" name="signposting" />
      ),
      value: 6,
    },
    {
      label: ReactDOMServer.renderToString(
        <Translator file="module-comsaEnvironmental" name="sewage.system" />
      ),
      value: 7,
    },
  ]);

  const [courseOccupancyTimeOptions] = useState([
    {
      label: ReactDOMServer.renderToString(
        <Translator file="module-comsaEnvironmental" name="temporary" />
      ),
      value: 0,
    },
    {
      label: ReactDOMServer.renderToString(
        <Translator file="module-comsaEnvironmental" name="permanent" />
      ),
      value: 1,
    },
  ]);

  // > LOAD DATA //
  const loadData = useCallback(
    async () =>
      axios
        .post(
          axiosPath("api_r_user_module_comsaEnvironmental_section_sheet_get"),
          { moduleBridgeHex, subModuleBridgeHex, plantHex, sheet: "dataInput" }
        )
        .then((r) => {
          if (isMounted.current) {
            setInitialValues(r.data.sheetData ?? {});
          }
        })
        .catch((e) => dispatch(errorHandler(e))),
    [moduleBridgeHex, subModuleBridgeHex, plantHex]
  );

  useEffect(() => {
    loadData();
  }, []);
  // < LOAD DATA //

  // > LOAD MODULE DATA //
  const loadModuleData = useCallback(async () => {
    axios
      .post(axiosPath("api_r_user_module_comsaEnvironmental_getModuleData"), {
        moduleBridgeHex,
        subModuleBridgeHex,
        plantHex,
      })
      .then((r) => {
        if (isMounted.current) {
          dispatch(currentPageAction.change("moduleData", r.data));
        }
      });
  }, []);

  useEffect(() => {
    loadModuleData();
  }, []);
  // > LOAD MODULE DATA //

  // > SAVE FORM //
  const handleSave = useCallback(
    async (payload) => {
      let updatedPayload = { ...payload };
      if (
        ![
          payload?.mainTipology,
          payload?.secondaryTipology,
          payload?.terciaryTipology,
          payload?.quaternaryTipology,
          payload?.quintaryTipology,
        ].includes(0) ||
        _.isEmpty(updatedPayload?.railwayConstruction)
      ) {
        updatedPayload = { ...updatedPayload, railwayConstruction: undefined };
      }
      if (
        ![
          payload?.mainTipology,
          payload?.secondaryTipology,
          payload?.terciaryTipology,
          payload?.quaternaryTipology,
          payload?.quintaryTipology,
        ].includes(1) ||
        _.isEmpty(updatedPayload?.civilConstruction)
      ) {
        updatedPayload = { ...updatedPayload, civilConstruction: undefined };
      }
      if (
        ![
          payload?.mainTipology,
          payload?.secondaryTipology,
          payload?.terciaryTipology,
          payload?.quaternaryTipology,
          payload?.quintaryTipology,
        ].includes(2) ||
        _.isEmpty(updatedPayload?.urbanization)
      ) {
        updatedPayload = { ...updatedPayload, urbanization: undefined };
      }
      if (
        ![
          payload?.mainTipology,
          payload?.secondaryTipology,
          payload?.terciaryTipology,
          payload?.quaternaryTipology,
          payload?.quintaryTipology,
        ].includes(3) ||
        _.isEmpty(updatedPayload?.building)
      ) {
        updatedPayload = { ...updatedPayload, building: undefined };
      }
      if (
        ![
          payload?.mainTipology,
          payload?.secondaryTipology,
          payload?.terciaryTipology,
          payload?.quaternaryTipology,
          payload?.quintaryTipology,
        ].includes(4) ||
        _.isEmpty(updatedPayload?.maritime)
      ) {
        updatedPayload = { ...updatedPayload, maritime: undefined };
      }
      axios
        .post(
          axiosPath("api_r_user_module_comsaEnvironmental_section_sheet_edit"),
          {
            moduleBridgeHex,
            subModuleBridgeHex,
            plantHex,
            payload: updatedPayload,
            sheet: "dataInput",
          }
        )
        .then((r) => {
          if (isMounted.current) {
            dispatch(
              currentPageAction.change("moduleData", r.data?.moduleData, true)
            );
            AppNotificationAlert({
              message: ReactDOMServer.renderToString(
                <Translator file="module-comsaEnvironmental" name="completed" />
              ),
              type: "success",
              duration: 3000,
            });
          }
        })
        .catch((e) => dispatch(errorHandler(e)));
    },
    [moduleBridgeHex, subModuleBridgeHex, plantHex]
  );
  // < SAVE FORM //

  // > DELETE SECTION //
  const handleDeleteSection = useCallback(
    async () =>
      axios
        .post(
          axiosPath("api_r_user_module_comsaEnvironmental_section_delete"),
          { moduleBridgeHex, subModuleBridgeHex, plantHex }
        )
        .then((r) => {
          dispatch(
            currentPageAction.change("moduleData", r.data?.moduleData, true)
          );
          navigate(
            path({
              name: "user_module_comsaEnvironmental_home",
              params: { moduleBridgeHex, subModuleBridgeHex, plantHex },
            })
          );
        })
        .catch((e) => dispatch(errorHandler(e))),
    [moduleBridgeHex, subModuleBridgeHex, plantHex]
  );
  // < DELETE SECTION //

  const handleReport = () => axios
    .post(
        axiosPath('api_r_user_module_comsaEnvironmental_dataInput'),
        {
        moduleBridgeHex,
        subModuleBridgeHex,
        plantHex,
        section: moduleData?.selectedSection?.name,
        }
    )
    .then(({ data }) => (window.location = data))
    .catch((e) => dispatch(errorHandler(e)))

  if (!moduleData) return <AppNoInfo />;

  return (
    <div className="pl-2">
      <BreadcrumbHandler
        paths={
          moduleData
            ? [
                {
                  name: `${
                    moduleData.plantName
                  } - ${ReactDOMServer.renderToString(
                    <Translator
                      file="module-legalCompliance"
                      name="evaluation"
                    />
                  )}`,
                  route: {
                    name: "user_module_legalCompliance_evaluation",
                    params: {
                      moduleBridgeHex,
                      plantHex,
                      page: 1,
                      filtersHex: 0,
                    },
                  },
                },
                {
                  name: ReactDOMServer.renderToString(
                    <Translator
                      file="module-comsaEnvironmental"
                      name="construction"
                    />
                  ),
                },
              ]
            : [{ name: "..." }]
        }
      />
      <section className="content pb-5">
        <ModuleComsaEnvironmentalSideBarReports activeRoute="user_module_comsaEnvironmental_construction_data_input" />
        <Title className="mb-4">
          <Translator
            file="module-comsaEnvironmental"
            name="data.collection.project"
          />
        </Title>
        {initialValues && (
          <Formik
            initialValues={initialValues}
            validateOnChange={false}
            validateOnBlur={false}
            onSubmit={handleSave}
          >
            {({
              setFieldValue,
              handleSubmit,
              values,
              errors,
              isSubmitting,
            }) => (
              <form onSubmit={handleSubmit}>
                <AppCard
                  header={
                    <>
                      <span>
                        <Translator
                          file="module-comsaEnvironmental"
                          name="general.data.work"
                        />
                      </span>
                      <span
                        className="btn btn-danger btn-xs float-right"
                        onClick={() => setShowModalDeleteSectionModal(true)}
                      >
                        <AppRemixIcon icon="delete-bin-6" />
                      </span>
                      <span
                        className="btn btn-success btn-xs float-right mr-1"
                        onClick={handleReport}
                      >
                        <AppRemixIcon icon="file-excel" />
                      </span>
                    </>
                  }
                  className="mb-2"
                >
                  <AppDatePicker
                    title={ReactDOMServer.renderToString(
                      <Translator
                        file="module-comsaEnvironmental"
                        name="date"
                      />
                    )}
                    locale={locale}
                    value={values.date}
                    error={errors.date}
                    onChange={(opt) => setFieldValue("date", opt)}
                    placeholder={ReactDOMServer.renderToString(
                      <Translator
                        file="module-comsaEnvironmental"
                        name="enter.date"
                      />
                    )}
                    isClearable
                    showMonthDropdown
                    showYearDropdown
                  />
                  <AppSingleSelect
                    options={noYesOptions}
                    title={ReactDOMServer.renderToString(
                      <Translator
                        file="module-comsaEnvironmental"
                        name="is.there.environmental.impact.study.project"
                      />
                    )}
                    value={values?.environmentalImpactStudy}
                    error={errors?.environmentalImpactStudy}
                    onChange={(opt) =>
                      setFieldValue("environmentalImpactStudy", opt)
                    }
                    isClearable
                  />
                  <AppMultiSelect
                    options={constructionScopeOptions}
                    title={ReactDOMServer.renderToString(
                      <Translator
                        file="module-comsaEnvironmental"
                        name="select.scope.project.indicate.all.affected.scopes"
                      />
                    )}
                    value={values?.constructionScope || []}
                    error={errors?.constructionScope}
                    onChange={(opt) => setFieldValue("constructionScope", opt)}
                    isClearable
                  />
                  <AppSingleSelect
                    options={noYesOptions}
                    title={ReactDOMServer.renderToString(
                      <Translator
                        file="module-comsaEnvironmental"
                        name="operation.infrastructure.planned.after.execution.project"
                      />
                    )}
                    value={values?.infrastructureExploitation}
                    error={errors?.infrastructureExploitation}
                    onChange={(opt) =>
                      setFieldValue("infrastructureExploitation", opt)
                    }
                    isClearable
                  />
                  <AppSingleSelect
                    options={noYesOptions}
                    title={ReactDOMServer.renderToString(
                      <Translator
                        file="module-comsaEnvironmental"
                        name="dia.applicable.project"
                      />
                    )}
                    value={values?.diaApplication}
                    error={errors?.diaApplication}
                    onChange={(opt) => setFieldValue("diaApplication", opt)}
                    isClearable
                  />
                  {values?.diaApplication === 1 && (
                    <div className="alert alert-warning">
                      <Translator
                        file="module-comsaEnvironmental"
                        name="send.dia.worldlex"
                      />
                    </div>
                  )}
                  <AppSingleSelect
                    options={noYesOptions}
                    title={ReactDOMServer.renderToString(
                      <Translator
                        file="module-comsaEnvironmental"
                        name="endangered.species"
                      />
                    )}
                    value={values?.extinctionDanger}
                    error={errors?.extinctionDanger}
                    onChange={(opt) => setFieldValue("extinctionDanger", opt)}
                    isClearable
                  />
                  {values?.extinctionDanger === 1 && (
                    <div className="pl-4">
                      <AppTextArea
                        title={ReactDOMServer.renderToString(
                          <Translator
                            file="module-comsaEnvironmental"
                            name="indicate.endangered.species"
                          />
                        )}
                        onChange={(opt) =>
                          setFieldValue("extinctionDangerText", opt)
                        }
                        value={values.extinctionDangerText}
                        error={errors?.extinctionDangerText}
                      />
                    </div>
                  )}
                  <AppSingleSelect
                    options={noYesOptions}
                    title={ReactDOMServer.renderToString(
                      <Translator
                        file="module-comsaEnvironmental"
                        name="is.there.design.stage"
                      />
                    )}
                    value={values?.designStage}
                    error={errors?.designStage}
                    onChange={(opt) => setFieldValue("designStage", opt)}
                    isClearable
                  />
                  <AppSingleSelect
                    options={noYesOptions}
                    title={ReactDOMServer.renderToString(
                      <Translator
                        file="module-comsaEnvironmental"
                        name="is.project.funded.next.generation.funds"
                      />
                    )}
                    value={values?.nextGenerationFunds}
                    error={errors?.nextGenerationFunds}
                    onChange={(opt) =>
                      setFieldValue("nextGenerationFunds", opt)
                    }
                    isClearable
                  />
                  <AppSingleSelect
                    options={controlInfluenceOptions}
                    title={ReactDOMServer.renderToString(
                      <Translator
                        file="module-comsaEnvironmental"
                        name="use.operation.maintenance.conservation.stage.capacity"
                      />
                    )}
                    value={values?.exploitationStageCapacityOf}
                    error={errors?.exploitationStageCapacityOf}
                    onChange={(opt) =>
                      setFieldValue("exploitationStageCapacityOf", opt)
                    }
                    isClearable
                  />
                  {values?.exploitationStageCapacityOf === 0 && (
                    <div className="alert alert-warning">
                      <Translator
                        file="module-comsaEnvironmental"
                        name="fill.tab.construction.normal.aspects"
                        isHtml
                      />
                      .
                    </div>
                  )}
                  {values?.exploitationStageCapacityOf === 1 && (
                    <div className="alert alert-warning">
                      <Translator
                        file="module-comsaEnvironmental"
                        name="fill.tab.normal.construction.aspects.normal.use.aspects"
                        isHtml
                      />
                      .
                    </div>
                  )}
                  <AppSingleSelect
                    options={controlInfluenceOptions}
                    title={ReactDOMServer.renderToString(
                      <Translator
                        file="module-comsaEnvironmental"
                        name="deconstruction.dismantling.stage.capacity"
                      />
                    )}
                    value={values?.deconstructionStageCapacityOf}
                    error={errors?.deconstructionStageCapacityOf}
                    onChange={(opt) =>
                      setFieldValue("deconstructionStageCapacityOf", opt)
                    }
                    isClearable
                  />
                  {values?.deconstructionStageCapacityOf === 0 && (
                    <div className="alert alert-warning">
                      <Translator
                        file="module-comsaEnvironmental"
                        name="fill.tab.construction.normal.aspects"
                        isHtml
                      />
                      .
                    </div>
                  )}
                  {values?.deconstructionStageCapacityOf === 1 && (
                    <div className="alert alert-warning">
                      <Translator
                        file="module-comsaEnvironmental"
                        name="fill.tab.normal.construction.aspects.normal.dismantling.aspects"
                        isHtml
                      />
                      .
                    </div>
                  )}
                </AppCard>
                <AppCard
                  header={ReactDOMServer.renderToString(
                    <Translator
                      file="module-comsaEnvironmental"
                      name="types.work"
                    />
                  )}
                  className="mb-2"
                >
                  <AppSingleSelect
                    options={tipologyOptions}
                    title={ReactDOMServer.renderToString(
                      <Translator
                        file="module-comsaEnvironmental"
                        name="indicate.main.typology"
                      />
                    )}
                    value={values?.mainTipology}
                    error={errors?.mainTipology}
                    onChange={(opt) => setFieldValue("mainTipology", opt)}
                    isClearable
                  />
                  <AppSingleSelect
                    options={tipologyOptions}
                    title={ReactDOMServer.renderToString(
                      <Translator
                        file="module-comsaEnvironmental"
                        name="indicate.secondary.typology"
                      />
                    )}
                    value={values?.secondaryTipology}
                    error={errors?.secondaryTipology}
                    onChange={(opt) => setFieldValue("secondaryTipology", opt)}
                    isClearable
                  />
                  <AppSingleSelect
                    options={tipologyOptions}
                    title={ReactDOMServer.renderToString(
                      <Translator
                        file="module-comsaEnvironmental"
                        name="indicate.tertiary.typology"
                      />
                    )}
                    value={values?.terciaryTipology}
                    error={errors?.terciaryTipology}
                    onChange={(opt) => setFieldValue("terciaryTipology", opt)}
                    isClearable
                  />
                  <AppSingleSelect
                    options={tipologyOptions}
                    title={ReactDOMServer.renderToString(
                      <Translator
                        file="module-comsaEnvironmental"
                        name="indicate.quaternary.typology"
                      />
                    )}
                    value={values?.quaternaryTipology}
                    error={errors?.quaternaryTipology}
                    onChange={(opt) => setFieldValue("quaternaryTipology", opt)}
                    isClearable
                  />
                  <AppSingleSelect
                    options={tipologyOptions}
                    title={ReactDOMServer.renderToString(
                      <Translator
                        file="module-comsaEnvironmental"
                        name="indicate.quinary.typology"
                      />
                    )}
                    value={values?.quintaryTipology}
                    error={errors?.quintaryTipology}
                    onChange={(opt) => setFieldValue("quintaryTipology", opt)}
                    isClearable
                  />
                </AppCard>
                {[
                  values?.mainTipology,
                  values?.secondaryTipology,
                  values?.terciaryTipology,
                  values?.quaternaryTipology,
                  values?.quintaryTipology,
                ].includes(0) && (
                  <ModuleComsaEnvironmentalRailwayConstruction
                    values={values?.railwayConstruction}
                    errors={errors?.railwayConstruction}
                    setFieldValue={(n, v) =>
                      setFieldValue(`railwayConstruction.${n}`, v)
                    }
                    noYesOptions={noYesOptions}
                    affectedServicesOptions={affectedServicesOptions}
                  />
                )}
                {[
                  values?.mainTipology,
                  values?.secondaryTipology,
                  values?.terciaryTipology,
                  values?.quaternaryTipology,
                  values?.quintaryTipology,
                ].includes(1) && (
                  <ModuleComsaEnvironmentalCivilConstruction
                    values={values?.civilConstruction}
                    errors={errors?.civilConstruction}
                    setFieldValue={(n, v) =>
                      setFieldValue(`civilConstruction.${n}`, v)
                    }
                    noYesOptions={noYesOptions}
                    affectedServicesOptions={affectedServicesOptions}
                    courseOccupancyTimeOptions={courseOccupancyTimeOptions}
                  />
                )}
                {[
                  values?.mainTipology,
                  values?.secondaryTipology,
                  values?.terciaryTipology,
                  values?.quaternaryTipology,
                  values?.quintaryTipology,
                ].includes(2) && (
                  <ModuleComsaEnvironmentalUrbanization
                    values={values?.urbanization}
                    errors={errors?.urbanization}
                    setFieldValue={(n, v) =>
                      setFieldValue(`urbanization.${n}`, v)
                    }
                    noYesOptions={noYesOptions}
                    affectedServicesOptions={affectedServicesOptions}
                    courseOccupancyTimeOptions={courseOccupancyTimeOptions}
                  />
                )}
                {[
                  values?.mainTipology,
                  values?.secondaryTipology,
                  values?.terciaryTipology,
                  values?.quaternaryTipology,
                  values?.quintaryTipology,
                ].includes(3) && (
                  <ModuleComsaEnvironmentalBuilding
                    values={values?.building}
                    errors={errors?.building}
                    setFieldValue={(n, v) => setFieldValue(`building.${n}`, v)}
                    noYesOptions={noYesOptions}
                    affectedServicesOptions={affectedServicesOptions}
                  />
                )}
                {[
                  values?.mainTipology,
                  values?.secondaryTipology,
                  values?.terciaryTipology,
                  values?.quaternaryTipology,
                  values?.quintaryTipology,
                ].includes(4) && (
                  <ModuleComsaEnvironmentalMaritime
                    values={values?.maritime}
                    errors={errors?.maritime}
                    setFieldValue={(n, v) => setFieldValue(`maritime.${n}`, v)}
                    noYesOptions={noYesOptions}
                    affectedServicesOptions={affectedServicesOptions}
                  />
                )}
                <SubmitWrapper>
                  <AppSubmitButton
                    className="btn btn-sm btn-success"
                    isSubmitting={isSubmitting}
                  >
                    <Translator file="module-comsaEnvironmental" name="save" />
                  </AppSubmitButton>
                </SubmitWrapper>
              </form>
            )}
          </Formik>
        )}
      </section>
      <AppDeleteModal
        show={showModalDeleteSectionModal}
        title={ReactDOMServer.renderToString(
          <Translator
            file="module-comsaEnvironmental"
            name="delete.all.project.data"
          />
        )}
        onClose={() => setShowModalDeleteSectionModal(false)}
        onConfirm={handleDeleteSection}
        sensitive
        sensitiveWord={deleteSectionPassword}
        sensitiveText={
          <Translator
            file="module-comsaEnvironmental"
            name="enter.the.password"
          />
        }
        password
      />
    </div>
  );
}

const Title = styled.h1`
  text-align: center;
  font-size: 30px;
`;

const SubmitWrapper = styled.div`
  position: fixed;
  right: 15px;
  bottom: 8px;
  z-index: 10;
`;
